/**
 * BulkUploadModal — generic CSV/XLSX bulk-import dialog for any partner asset type.
 * Calls:
 *   GET  /api/partner/{asset_type}/bulk-template?format=csv|xlsx
 *   POST /api/partner/{asset_type}/bulk-upload (multipart)
 *
 * Notes:
 *   • Uses fetch() for template download because the response is a binary stream;
 *     avoids axios responseType weirdness and lets the browser save the file.
 *   • POST upload sends FormData via axios so getAuthHeaders() carries the bearer token.
 */
import { useState, useRef } from 'react';
import { Download, Upload, X, Loader2, FileSpreadsheet, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getAuthHeaders, getAuthToken } from '@/utils/auth';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSET_LABELS = {
  cars: 'Cars',
  yachts: 'Yachts',
  portfolio: 'Portfolio Projects',
  services: 'Services',
};

const BulkUploadModal = ({ open, assetType, onClose, onComplete }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [result, setResult] = useState(null);

  if (!open) return null;
  const label = ASSET_LABELS[assetType] || assetType;

  const downloadTemplate = async (format) => {
    setDownloading(format);
    try {
      const res = await fetch(`${API}/partner/${assetType}/bulk-template?format=${format}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dreamlife_${assetType}_template.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} template downloaded`);
    } catch (err) {
      toast.error('Could not download template');
    } finally {
      setDownloading(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Pick a CSV or XLSX file first');
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/partner/${assetType}/bulk-upload`,
        fd,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      setResult(res.data);
      if (res.data.total > 0) {
        toast.success(`Uploaded ${res.data.total} ${label.toLowerCase()}`);
        onComplete?.();
      } else {
        toast.error('No rows were imported. Check the errors below.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" data-testid={`bulk-upload-modal-${assetType}`}>
      <div className="bg-obsidian border border-white/10 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-6 h-6 text-gold" />
            <h3 className="text-xl font-playfair font-semibold">Bulk Upload {label}</h3>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary" data-testid="bulk-upload-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Step 1 */}
          <div>
            <p className="text-sm uppercase tracking-wider text-gold mb-2">Step 1 · Download template</p>
            <p className="text-sm text-text-secondary mb-3">
              Start with our template — fill in your {label.toLowerCase()}, save, and upload below.
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => downloadTemplate('csv')}
                disabled={downloading === 'csv'}
                className="border-gold/30 text-gold hover:bg-gold/10"
                data-testid={`bulk-template-csv-${assetType}`}
              >
                {downloading === 'csv' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                CSV Template
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => downloadTemplate('xlsx')}
                disabled={downloading === 'xlsx'}
                className="border-gold/30 text-gold hover:bg-gold/10"
                data-testid={`bulk-template-xlsx-${assetType}`}
              >
                {downloading === 'xlsx' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Excel Template
              </Button>
            </div>
          </div>

          {/* Step 2 */}
          <div>
            <p className="text-sm uppercase tracking-wider text-gold mb-2">Step 2 · Upload your file</p>
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-white/15 hover:border-gold/40 rounded-lg p-8 text-center cursor-pointer transition-colors"
              data-testid={`bulk-upload-dropzone-${assetType}`}
            >
              <Upload className="w-8 h-8 text-text-secondary mx-auto mb-2" />
              <p className="text-text-secondary text-sm">{file ? file.name : 'Click to choose a CSV or XLSX file'}</p>
              <p className="text-text-muted text-xs mt-1">Max ~10 MB</p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                hidden
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
            <Button
              type="button"
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full mt-4 bg-gold text-black hover:bg-gold-light"
              data-testid={`bulk-upload-submit-${assetType}`}
            >
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : `Import ${label}`}
            </Button>
          </div>

          {/* Step 3 — results */}
          {result && (
            <div className="border-t border-white/10 pt-4 space-y-3" data-testid={`bulk-upload-result-${assetType}`}>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3">
                  <div className="flex items-center gap-2 text-emerald-400 text-sm">
                    <CheckCircle2 className="w-4 h-4" /> Imported
                  </div>
                  <p className="text-2xl font-bold text-emerald-400 mt-1">{result.total}</p>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertTriangle className="w-4 h-4" /> Failed
                  </div>
                  <p className="text-2xl font-bold text-red-400 mt-1">{result.failed}</p>
                </div>
              </div>
              {result.errors?.length > 0 && (
                <div className="max-h-40 overflow-y-auto text-xs space-y-1 bg-white/[0.02] rounded p-3">
                  {result.errors.slice(0, 20).map((e, i) => (
                    <p key={i} className="text-red-400/80">Row {e.row}: {e.errors.join(', ')}</p>
                  ))}
                  {result.errors.length > 20 && (
                    <p className="text-text-muted italic">…and {result.errors.length - 20} more.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
